// src/App.js
import { useState, useEffect, lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { LinearProgress, Box } from "@mui/material";
import { SWRConfig } from "swr";

// ✅ Layout y config: NO lazy (para que el sidenav nunca “desaparezca”)
import Sidenav from "examples/Sidenav";
import FloatingSidenavToggle from "layouts/dashboard/components/FloatingSidenavToggle";
import routes from "routes";
import { useMaterialUIController } from "context";
import lightTheme from "assets/theme";
import darkTheme from "assets/theme-dark";

// ✅ Guards: también NO lazy para no bloquear navegación
import RutaProtegida from "features/auth/components/RutaProtegida";
import RequirePerms from "features/auth/components/RequirePerms";

// ✅ Sucursal operativa: contexto y gate tampoco lazy (deben estar listos
// junto con el sidenav, no después de que cargue una página)
import { SucursalOperativaProvider } from "features/sucursales/context/SucursalOperativaContext";
import SeleccionarSucursalGate from "features/sucursales/components/SeleccionarSucursalGate";

import "./styles.css";

/* ================= Lazy solo en PÁGINAS ================= */
const Dashboard = lazy(() => import("layouts/dashboard"));
const LoginERP = lazy(() => import("features/auth/pages/LoginERP"));
const Logout = lazy(() => import("features/auth/pages/Logout"));
const ForgotPassword = lazy(() => import("features/auth/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("features/auth/pages/ResetPassword"));

const Ventas = lazy(() => import("features/ventas/pages/Ventas"));
const HistorialVentas = lazy(() => import("features/ventas/pages/HistorialVentas"));
const ProductosVendidos = lazy(() => import("features/ventas/pages/ProductosVendidos"));

const AltaTelefono = lazy(() => import("features/productos/pages/AltaTelefono"));
const InventarioPage = lazy(() => import("features/productos/pages/InventarioPage"));
const HistorialTabla = lazy(() => import("features/productos/pages/TablaHistorial"));
const MantenimientoPage = lazy(() => import("features/reparaciones/pages/MantenimientoPage"));
// CP4-COSTOS: grupos de costo promedio (lectura + grupos personalizados vacíos).
const GruposCostoPage = lazy(() => import("features/costos/pages/GruposCostoPage"));
const AccesoriosPage = lazy(() => import("features/accesorios/pages/AccesoriosPage"));

const Garantias = lazy(() => import("features/garantias/pages/Garantias"));
const ReportesGananciasPage = lazy(() =>
  import("features/reportes/pages/ReportesGananciasPage")
);
const ComprasPage = lazy(() => import("features/compras/pages/ComprasPage"));
const NuevaCompraPage = lazy(() => import("features/compras/pages/NuevaCompraPage"));
const ProveedoresPage = lazy(() => import("features/proveedores/pages/ProveedoresPage"));
const CuentaCorrientePage = lazy(() => import("features/proveedores/pages/CuentaCorrientePage"));
const PagosProveedoresPage = lazy(() => import("features/proveedores/pages/PagosProveedoresPage"));
const CuentaCorrienteClientesPage = lazy(() => import("features/clientes/pages/CuentaCorrienteClientesPage"));

const SucursalesPage = lazy(() => import("features/sucursales/pages/SucursalesPage"));

/* ============== Fallback global liviano =================== */
function PageBoot() {
  return (
    <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 2000 }}>
      <LinearProgress />
    </Box>
  );
}

/* ============== Fetcher global para SWR =================== */
const swrFetcher = (url) =>
  fetch(url, { credentials: "include" }).then((r) => {
    if (!r.ok) throw new Error("Network error");
    return r.json();
  });

export default function App() {
  const [controller] = useMaterialUIController();
  const { layout, sidenavColor, darkMode } = controller;

  const { pathname } = useLocation();

    

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Ocultar sidenav en pantallas de auth
  const isAuthPage =
    pathname === "/login-erp" || pathname === "/olvide-clave" || pathname === "/reset";

  const resolvedTheme = darkMode ? darkTheme : lightTheme;

  // Prefetch del bundle del Dashboard cuando ya no estás en pantallas de auth
  useEffect(() => {
    if (!isAuthPage) {
      import("layouts/dashboard");
    }
  }, [isAuthPage]);

  return (
    <ThemeProvider theme={resolvedTheme}>
      <SWRConfig
        value={{
          fetcher: swrFetcher,
          revalidateOnFocus: false,     // no re-fetch al cambiar de pestaña
          shouldRetryOnError: false,    // sin reintentos ruidosos
          dedupingInterval: 1500,       // de-dupe de requests cercanas
          errorRetryCount: 0,
        }}
      >
        <CssBaseline />

        <SucursalOperativaProvider>
          {/* ✅ Sidenav nunca desaparece por lazy */}
          {!isAuthPage && layout === "dashboard" && (
            <Sidenav color={sidenavColor} brandName="Dashboard" routes={routes} />
          )}
          {!isAuthPage && <FloatingSidenavToggle />}
          {!isAuthPage && <SeleccionarSucursalGate />}

          {/* ✅ Sólo las páginas están bajo Suspense */}
          <Suspense fallback={<PageBoot />}>
            <Routes>
            {/* Dashboard */}
            <Route
              path="/dashboard"
              element={
                <RutaProtegida>
                  <Dashboard />
                </RutaProtegida>
              }
            />

            {/* Productos */}
            <Route
              path="/alta-productos"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["productos:create"]}>
                    <AltaTelefono />
                  </RequirePerms>
                </RutaProtegida>
              }
            />
            <Route
              path="/inventario"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["inventario:read"]}>
                    <InventarioPage />
                  </RequirePerms>
                </RutaProtegida>
              }
            />
            <Route
              path="/accesorios"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["inventario:read"]}>
                    <AccesoriosPage />
                  </RequirePerms>
                </RutaProtegida>
              }
            />
            <Route
              path="/historial"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["inventario:read"]}>
                    <HistorialTabla />
                  </RequirePerms>
                </RutaProtegida>
              }
            />
            {/* Mantenimiento — backend (api/reparaciones/index.php) sólo
               exige sesión autenticada, sin permiso específico (ver
               informe). Se usa "inventario:read" acá para mantener el
               patrón del repo (toda ruta protegida declara un perm) sin
               inventar una restricción que el backend no aplica. */}
            <Route
              path="/mantenimiento"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["inventario:read"]}>
                    <MantenimientoPage />
                  </RequirePerms>
                </RutaProtegida>
              }
            />
            {/* CP4.4: misma lectura que el backend (costos:read O reportes:read); administrar exige costos:manage en el backend. */}
            <Route
              path="/grupos-costo"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["costos:read", "reportes:read"]} alguno>
                    <GruposCostoPage />
                  </RequirePerms>
                </RutaProtegida>
              }
            />

            {/* Ventas */}
            <Route
              path="/cargar-venta"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["ventas:create"]}>
                    <Ventas />
                  </RequirePerms>
                </RutaProtegida>
              }
            />
            <Route
              path="/ventas-realizadas"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["ventas:read"]}>
                    <HistorialVentas />
                  </RequirePerms>
                </RutaProtegida>
              }
            />
            <Route
              path="/productos-vendidos"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["ventas:read"]}>
                    <ProductosVendidos />
                  </RequirePerms>
                </RutaProtegida>
              }
            />

            {/* Compras */}
            <Route
              path="/compras"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["compras:read"]}>
                    <ComprasPage />
                  </RequirePerms>
                </RutaProtegida>
              }
            />
            <Route
              path="/compras/nueva"
              element={
                <RutaProtegida>
                  {/* CP6/CP2.5: crear una compra exige compras:create (backend igual). */}
                  <RequirePerms perms={["compras:read", "compras:create"]}>
                    <NuevaCompraPage />
                  </RequirePerms>
                </RutaProtegida>
              }
            />

            {/* Proveedores */}
            <Route
              path="/proveedores"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["proveedores:read"]}>
                    <ProveedoresPage />
                  </RequirePerms>
                </RutaProtegida>
              }
            />
            <Route
              path="/proveedores/cuenta-corriente"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["proveedores:read"]}>
                    <CuentaCorrientePage />
                  </RequirePerms>
                </RutaProtegida>
              }
            />
            <Route
              path="/proveedores/pagos"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["proveedores:read"]}>
                    <PagosProveedoresPage />
                  </RequirePerms>
                </RutaProtegida>
              }
            />

            {/* Cuenta corriente clientes */}
            <Route
              path="/clientes/cuenta-corriente"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["ventas:read"]}>
                    <CuentaCorrienteClientesPage />
                  </RequirePerms>
                </RutaProtegida>
              }
            />

            {/* Garantías */}
            <Route
              path="/garantias"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["garantias:read"]}>
                    <Garantias />
                  </RequirePerms>
                </RutaProtegida>
              }
            />

            {/* Reportes */}
            <Route
              path="/costos-ganancias"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["reportes:read"]}>
                    <ReportesGananciasPage />
                  </RequirePerms>
                </RutaProtegida>
              }
            />

            {/* Sucursales */}
            <Route
              path="/sucursales"
              element={
                <RutaProtegida>
                  <RequirePerms perms={["sucursales:manage"]}>
                    <SucursalesPage />
                  </RequirePerms>
                </RutaProtegida>
              }
            />

            {/* Auth */}
            <Route path="/login-erp" element={<LoginERP />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/forgot" element={<ForgotPassword />} />
            <Route path="/reset" element={<ResetPassword />} />

            {/* Default: ir directo al login */}
            <Route path="/" element={<Navigate to="/login-erp" replace />} />
            {/* Cualquier ruta inexistente también al login */}
            <Route path="*" element={<Navigate to="/login-erp" replace />} />
            </Routes>
          </Suspense>
        </SucursalOperativaProvider>
      </SWRConfig>
    </ThemeProvider>
  );
}
